import type { ExecutedOrder } from '@dx-private/dxchart5-react/dist/chart/model/executed-orders.model'
import type {
  OrderId,
  OrderType as DxOrderType,
  OrderWithId,
  OrderSide as DxOrderSide,
  ProtectionOrderType,
} from '@dx-private/dxchart5-react/dist/chart/model/trading/order.model'
import type { Position } from '@dx-private/dxchart5-react/dist/chart/model/trading/position.model'
import type { OperationClosed, OperationOpen, OperationOrder } from '../sdk'
import type { MergedSymbolInfo } from './dx-trading-mappers'
import { computeUnityUnrealizedPnlUsd } from './unity-unrealized-pnl'

export type SnapshotState = 'pending' | 'open'

export interface OperationSnapshot {
  id: string
  symbol: string
  state: SnapshotState
  side: DxOrderSide
  quantity: number
  orderType: DxOrderType
  targetPrice?: number
  stopLossPrice?: number
  takeProfitPrice?: number
}

const toNumber = (value: string | undefined): number | undefined => {
  if (!value) {
    return undefined
  }
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

export const toDxSide = (side: 'BUY' | 'SELL'): DxOrderSide => (side === 'BUY' ? 'buy' : 'sell')

export const toBackendSide = (side: DxOrderSide): 'BUY' | 'SELL' => (side === 'buy' ? 'BUY' : 'SELL')

export const toDxOrderType = (type: 'MARKET' | 'LIMIT' | 'STOP'): DxOrderType => {
  switch (type) {
    case 'MARKET':
      return 'market'
    case 'STOP':
      return 'stop'
    case 'LIMIT':
    default:
      return 'limit'
  }
}

export const toBackendPendingOrderType = (type: DxOrderType): 'LIMIT' | 'STOP' =>
  type === 'limit' ? 'LIMIT' : 'STOP'

export const createProtectionOrderId = (
  originalId: string,
  type: ProtectionOrderType
): OrderId => `protection::${originalId}::${type}`

export const getOrderPrice = (order: {
  orderType: DxOrderType
  limitPrice?: number
  stopPrice?: number
}): number | undefined => {
  if (order.orderType === 'limit') {
    return order.limitPrice
  }
  if (order.orderType === 'stop') {
    return order.stopPrice
  }
  return undefined
}

export const asBackendPriceString = (value: number | undefined): string | undefined => {
  if (value === undefined || !Number.isFinite(value)) {
    return undefined
  }
  return value.toString()
}

const toProtectionIds = (
  snapshot: OperationSnapshot
): [OrderId | undefined, OrderId | undefined] | undefined => {
  const slId = snapshot.stopLossPrice !== undefined ? createProtectionOrderId(snapshot.id, 'sl') : undefined
  const tpId = snapshot.takeProfitPrice !== undefined ? createProtectionOrderId(snapshot.id, 'tp') : undefined
  if (!slId && !tpId) {
    return undefined
  }
  return [slId, tpId]
}

const oppositeSide = (side: DxOrderSide): DxOrderSide => (side === 'buy' ? 'sell' : 'buy')

export const pendingOperationToSnapshot = (op: OperationOrder): OperationSnapshot => ({
  id: op.id,
  symbol: op.symbol,
  state: 'pending',
  side: toDxSide(op.side),
  quantity: Number(op.lots),
  orderType: toDxOrderType(op.type),
  targetPrice: toNumber(op.targetPrice),
  stopLossPrice: toNumber(op.stopLossPrice),
  takeProfitPrice: toNumber(op.takeProfitPrice),
})

export const openOperationToSnapshot = (op: OperationOpen): OperationSnapshot => ({
  id: op.order.id,
  symbol: op.order.symbol,
  state: 'open',
  side: toDxSide(op.order.side),
  quantity: Number(op.order.lots),
  orderType: 'market',
  targetPrice: toNumber(op.order.targetPrice),
  stopLossPrice: toNumber(op.order.stopLossPrice),
  takeProfitPrice: toNumber(op.order.takeProfitPrice),
})

export const buildSnapshotMap = (
  pendingOrders: OperationOrder[],
  openPositions: OperationOpen[]
): Map<string, OperationSnapshot> => {
  const snapshots = new Map<string, OperationSnapshot>()
  pendingOrders.forEach((op) => {
    snapshots.set(op.id, pendingOperationToSnapshot(op))
  })
  openPositions.forEach((op) => {
    snapshots.set(op.order.id, openOperationToSnapshot(op))
  })
  return snapshots
}

export const pendingOrderToDxOriginalOrder = (op: OperationOrder): OrderWithId | null => {
  // Layer 2: Rendering Guard - Skip non-active orders to prevent displaying cancelled/failed orders
  if (op.status !== 'ACTIVE') {
    return null
  }

  const snapshot = pendingOperationToSnapshot(op)
  const order: OrderWithId = {
    id: snapshot.id,
    type: 'original',
    orderType: snapshot.orderType,
    side: snapshot.side,
    quantity: snapshot.quantity,
    data: {
      symbol: snapshot.symbol,
      state: snapshot.state,
      operationId: snapshot.id,
      readonly: 'true',
      source: 'tournament-store',
      status: op.status, // Preserve status in dxChart data for debugging/validation
    },
  }

  if (snapshot.orderType === 'limit' && snapshot.targetPrice !== undefined) {
    order.limitPrice = snapshot.targetPrice
  }
  if (snapshot.orderType === 'stop' && snapshot.targetPrice !== undefined) {
    order.stopPrice = snapshot.targetPrice
  }

  const protectionOrderIds = toProtectionIds(snapshot)
  if (protectionOrderIds) {
    order.protectionOrderIds = protectionOrderIds
  }

  return order
}

export const snapshotToProtectionOrders = (snapshot: OperationSnapshot): OrderWithId[] => {
  const orders: OrderWithId[] = []

  if (snapshot.stopLossPrice !== undefined) {
    orders.push({
      id: createProtectionOrderId(snapshot.id, 'sl'),
      type: 'sl',
      originalItemId: snapshot.id,
      orderType: 'limit',
      side: oppositeSide(snapshot.side),
      quantity: snapshot.quantity,
      limitPrice: snapshot.stopLossPrice,
      data: {
        symbol: snapshot.symbol,
        state: snapshot.state,
        operationId: snapshot.id,
        readonly: 'true',
        source: 'tournament-store',
        protectionType: 'sl',
      },
    })
  }

  if (snapshot.takeProfitPrice !== undefined) {
    orders.push({
      id: createProtectionOrderId(snapshot.id, 'tp'),
      type: 'tp',
      originalItemId: snapshot.id,
      orderType: 'limit',
      side: oppositeSide(snapshot.side),
      quantity: snapshot.quantity,
      limitPrice: snapshot.takeProfitPrice,
      data: {
        symbol: snapshot.symbol,
        state: snapshot.state,
        operationId: snapshot.id,
        readonly: 'true',
        source: 'tournament-store',
        protectionType: 'tp',
      },
    })
  }

  return orders
}

export const openPositionToDxPosition = (
  op: OperationOpen,
  mergedSymbols: Record<string, MergedSymbolInfo>,
  latestPrices: Record<string, number>
): Position => {
  const snapshot = openOperationToSnapshot(op)
  const backendPnlUsd = Number(op.unrealizedPnl)
  const fallbackPnlUsd = Number.isFinite(backendPnlUsd) ? backendPnlUsd : 0

  // Extract metadata for symbol-specific pip size, contract size, and conversion behavior
  // Fallback chain: computed PnL (metadata-driven) → backend PnL (fallback)
  // If computed PnL returns null (missing conversion prices), we use backend PnL directly
  const symbolMetadata = mergedSymbols[op.order.symbol]?.backend

  const pl =
    computeUnityUnrealizedPnlUsd({
      symbol: op.order.symbol,
      side: op.order.side,
      entryPrice: Number(op.openPrice),
      lots: Number(op.order.lots),
      currentPrice: Number(latestPrices[op.order.symbol]),
      latestPricesBySymbol: latestPrices,
      metadata: symbolMetadata,
    }) ?? fallbackPnlUsd

  const position: Position = {
    id: snapshot.id,
    type: 'original',
    side: snapshot.side,
    quantity: snapshot.quantity,
    price: Number(op.openPrice),
    pl,
    data: {
      symbol: snapshot.symbol,
      operationId: snapshot.id,
      readonly: 'true',
      source: 'tournament-store',
    },
  }

  const protectionOrderIds = toProtectionIds(snapshot)
  if (protectionOrderIds) {
    position.protectionOrderIds = protectionOrderIds
  }

  return position
}

export const closedPositionToExecutedOrder = (op: OperationClosed): ExecutedOrder => ({
  id: op.order.id,
  timestamp: new Date(op.closedAt).getTime(),
  price: Number(op.closePrice),
  side: toDxSide(op.order.side),
  quantity: Number(op.order.lots),
})

const toOppositeDxSide = (side: 'BUY' | 'SELL'): DxOrderSide =>
  side === 'BUY' ? 'sell' : 'buy'

export const openPositionToEntryExecutedOrder = (
  op: OperationOpen,
  timestampOffsetMs: number = 0,
): ExecutedOrder => ({
  id: `${op.order.id}::open`,
  timestamp: new Date(op.openedAt).getTime() + timestampOffsetMs,
  price: Number(op.openPrice),
  side: toDxSide(op.order.side),
  quantity: Number(op.order.lots),
})

export const closedPositionToEntryExecutedOrder = (op: OperationClosed): ExecutedOrder => ({
  id: `${op.order.id}::open`,
  timestamp: new Date(op.openedAt).getTime(),
  price: Number(op.openPrice),
  side: toDxSide(op.order.side),
  quantity: Number(op.order.lots),
})

export const closedPositionToExitExecutedOrder = (op: OperationClosed): ExecutedOrder => ({
  id: `${op.order.id}::close`,
  timestamp: new Date(op.closedAt).getTime(),
  price: Number(op.closePrice),
  side: toOppositeDxSide(op.order.side),
  quantity: Number(op.order.lots),
})
